import { useNavigate, useLocation } from 'react-router-dom';
import { Home, BookOpen, Users, Calendar, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useTouchGestures';

interface NavigationItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  badge?: number;
}

interface MobileBottomNavigationProps {
  userRole?: string;
}

export const MobileBottomNavigation = ({ userRole }: MobileBottomNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerHaptic } = useHapticFeedback();

  const navigationItems: NavigationItem[] = [
    {
      icon: Home,
      label: 'Início',
      path: '/'
    },
    {
      icon: BookOpen,
      label: 'Casos',
      path: '/cases'
    },
    {
      icon: Users,
      label: 'Clientes',
      path: '/clients'
    },
    {
      icon: Calendar,
      label: 'Agenda',
      path: '/agenda'
    },
    {
      icon: Settings,
      label: 'Mais',
      path: '/settings'
    }
  ];

  const handleNavigation = (path: string) => {
    triggerHaptic('light');
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Spacer para evitar que o conteúdo fique atrás da navegação */}
      <div className="h-16 xs:h-14 sm:h-16 md:hidden" />
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-t border-gray-200 md:hidden ios-safe-area-bottom ios-nav pwa-bottom-nav">
        <div className="flex items-center justify-around h-16 xs:h-14 sm:h-16">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 h-full relative',
                  'touch-action-manipulation mobile-button transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  'ios-touch-target ios-no-highlight ios-touch-feedback',
                  active
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                )}
                aria-label={item.label}
              >
                <div className="relative">
                  <Icon 
                    className={cn(
                      'w-5 h-5 xs:w-6 xs:h-6 transition-all duration-200',
                      active ? 'scale-110' : 'scale-100'
                    )} 
                  />
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 xs:-top-2 xs:-right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 xs:w-5 xs:h-5 flex items-center justify-center">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span 
                  className={cn(
                    'text-xs xs:text-xs mt-1 transition-all duration-200 ios-text-responsive',
                    active ? 'font-semibold' : 'font-normal'
                  )}
                >
                  {item.label}
                </span>
                
                {/* Active indicator */}
                {active && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-6 xs:w-8 h-1 bg-primary rounded-b-full" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

// Componente de navegação móvel com swipe
export const SwipeableBottomNavigation = ({ userRole }: MobileBottomNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerHaptic } = useHapticFeedback();

  const navigationItems: NavigationItem[] = [
    { icon: Home, label: 'Início', path: '/' },
    { icon: BookOpen, label: 'Casos', path: '/cases' },
    { icon: Users, label: 'Clientes', path: '/clients' },
    { icon: Calendar, label: 'Agenda', path: '/agenda' },
    { icon: Settings, label: 'Mais', path: '/settings' }
  ];

  const currentIndex = navigationItems.findIndex(item => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(item.path);
  });

  const handleSwipeLeft = () => {
    const nextIndex = Math.min(currentIndex + 1, navigationItems.length - 1);
    if (nextIndex !== currentIndex) {
      triggerHaptic('medium');
      navigate(navigationItems[nextIndex].path);
    }
  };

  const handleSwipeRight = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    if (prevIndex !== currentIndex) {
      triggerHaptic('medium');
      navigate(navigationItems[prevIndex].path);
    }
  };

  return (
    <>
      {/* Spacer */}
      <div className="h-16 md:hidden" />
      
      {/* Swipeable Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 md:hidden ios-safe-area-bottom">
        <div 
          className="flex items-center justify-around h-16 swipe-container"
          onTouchStart={(e) => {
            const startX = e.touches[0].clientX;
            const handleTouchMove = (moveEvent: TouchEvent) => {
              const currentX = moveEvent.touches[0].clientX;
              const diff = startX - currentX;
              
              if (Math.abs(diff) > 50) {
                if (diff > 0) {
                  handleSwipeLeft();
                } else {
                  handleSwipeRight();
                }
                document.removeEventListener('touchmove', handleTouchMove);
              }
            };
            
            document.addEventListener('touchmove', handleTouchMove, { passive: true });
            document.addEventListener('touchend', () => {
              document.removeEventListener('touchmove', handleTouchMove);
            }, { once: true });
          }}
        >
          {navigationItems.map((item, index) => {
            const Icon = item.icon;
            const active = index === currentIndex;
            
            return (
              <button
                key={item.path}
                onClick={() => {
                  triggerHaptic('light');
                  navigate(item.path);
                }}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 h-full relative',
                  'touch-action-manipulation mobile-button transition-all duration-300',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  active
                    ? 'text-primary transform scale-105'
                    : 'text-gray-500 hover:text-gray-700'
                )}
                aria-label={item.label}
              >
                <div className="relative">
                  <Icon 
                    className={cn(
                      'w-6 h-6 transition-all duration-300',
                      active ? 'scale-110' : 'scale-100'
                    )} 
                  />
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span 
                  className={cn(
                    'text-xs mt-1 transition-all duration-300',
                    active ? 'font-semibold opacity-100' : 'font-normal opacity-70'
                  )}
                >
                  {item.label}
                </span>
                
                {/* Active indicator with animation */}
                {active && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-primary rounded-b-full animate-in slide-in-from-top-1 duration-300" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Swipe indicator */}
        <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gray-300 rounded-full" />
      </nav>
    </>
  );
};